import type { CriticalResultsKpiItem } from '../components/criticalResultsDashboard/CriticalResultsKpiGrid';
import type {
  CriticalResultsDashboardSummary,
  CriticalResultsKpiKey,
} from '../types/criticalResultsDashboard.types';
import { formatShortDuration } from './criticalResultsDashboardUtils';

export function getExpectedKpiCount(
  summary: CriticalResultsDashboardSummary | null | undefined,
  kpiKey: CriticalResultsKpiKey | null | undefined
): number | null {
  if (!summary || !kpiKey) {
    return null;
  }

  switch (kpiKey) {
    case 'open':
      return summary.openAlerts;
    case 'pending':
      return summary.pendingAcknowledgements;
    case 'acknowledged':
      return summary.acknowledgedAlerts;
    case 'escalated':
      return summary.escalatedAlerts;
    case 'breaches':
      return summary.slaBreaches;
    case 'withinSla':
      return summary.withinSlaAlerts;
    case 'beyondSla':
      return Math.max(0, summary.openAlerts - summary.withinSlaAlerts);
    case 'cancelled':
      return summary.cancelledAlerts;
    default:
      return null;
  }
}

export function buildCriticalResultsKpiItems(
  summary: CriticalResultsDashboardSummary | null | undefined
): CriticalResultsKpiItem[] {
  const avgTat = summary?.averageTatSeconds ?? 0;
  const medianTat = summary?.medianTatSeconds ?? 0;

  return [
    {
      key: 'open',
      label: 'Open critical alerts',
      value: String(summary?.openAlerts ?? 0),
      clickable: true,
    },
    {
      key: 'pending',
      label: 'Pending acknowledgement',
      value: String(summary?.pendingAcknowledgements ?? 0),
      clickable: true,
    },
    {
      key: 'acknowledged',
      label: 'Acknowledged',
      value: String(summary?.acknowledgedAlerts ?? 0),
      clickable: true,
    },
    {
      key: 'escalated',
      label: 'Escalated alerts',
      value: String(summary?.escalatedAlerts ?? 0),
      tone: 'warning',
      clickable: true,
    },
    {
      key: 'breaches',
      label: 'SLA breaches',
      value: String(summary?.slaBreaches ?? 0),
      tone: 'danger',
      clickable: true,
    },
    {
      key: 'avgTat',
      label: 'Avg acknowledgement TAT',
      value: formatShortDuration(avgTat),
      subText: `Median ${formatShortDuration(medianTat)}`,
      clickable: false,
    },
    {
      key: 'withinSla',
      label: 'Within SLA',
      value: String(summary?.withinSlaAlerts ?? 0),
      clickable: false,
    },
    {
      key: 'beyondSla',
      label: 'Beyond SLA',
      value: String(Math.max(0, (summary?.openAlerts ?? 0) - (summary?.withinSlaAlerts ?? 0))),
      tone: 'warning',
      clickable: true,
    },
  ];
}
